import { QueryClient } from '@tanstack/react-query';

import {
  WS_CHANNEL_ALERT,
  WS_CHANNEL_MISSION,
  WS_CHANNEL_ROADMAP,
  WS_CHANNEL_VEHICLE_STATES,
} from '../constants.ts';
import {
  alertMessageSchema,
  MissionMessage,
  missionMessageSchema,
  RoadmapMessage,
  roadmapMessageSchema,
  VehicleMessage,
  vehicleMessageSchema,
} from '../schema/Message.ts';
import { IMission } from '../schema/Mission.ts';
import { IVehicle } from '../schema/Vehicle.ts';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private queryClient: QueryClient;
  private token: string | null = null;
  private territorySlug: string | null = null;

  constructor(url: string, queryClient: QueryClient) {
    this.url = url;
    this.queryClient = queryClient;
  }

  connect(token: string, territorySlug: string) {
    this.token = token;
    this.territorySlug = territorySlug;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendInit();
      return;
    }

    this.ws = new WebSocket(this.url);

    this.ws.addEventListener('open', () => this.sendInit());
    this.ws.addEventListener('message', msg => this.onMessage(msg));
    this.ws.addEventListener('close', () => {
      setTimeout(() => {
        if (this.token && this.territorySlug) this.connect(this.token, this.territorySlug);
      }, 3000);
    });
  }

  private sendInit() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!this.token || !this.territorySlug) return;

    this.ws.send(
      JSON.stringify({
        type: 'init',
        auth_token: this.token,
        territory_slug: this.territorySlug,
      }),
    );
  }

  private onMessage(msg: MessageEvent) {
    const data = JSON.parse(msg.data);
    switch (data.message_type) {
      case WS_CHANNEL_VEHICLE_STATES:
        if (vehicleMessageSchema.safeParse(data).success) {
          this.dispatchVehicle(data as VehicleMessage);
        }
        break;
      case WS_CHANNEL_MISSION:
        if (missionMessageSchema.safeParse(data).success) {
          this.dispatchMission(data as MissionMessage);
        }
        break;
      case WS_CHANNEL_ROADMAP:
        if (roadmapMessageSchema.safeParse(data).success) {
          this.dispatchRoadmap(data as RoadmapMessage);
        }
        break;
      case WS_CHANNEL_ALERT:
        if (alertMessageSchema.safeParse(data).success) {
          this.dispatchAlert();
        }
        break;
    }
  }
  private dispatchVehicle(message: VehicleMessage) {
    this.queryClient.setQueryData(
      ['vehicles', this.territorySlug],
      (oldData: { data: IVehicle[] } | undefined) => {
        if (!oldData?.data) return oldData;

        const updatedData = oldData.data.map(vehicle =>
          vehicle.shuttle_provider_identifier === message.provider_identifier
            ? {
                ...vehicle,
                charge_percentage: message.battery.level,
                door_status: message.door_status ?? vehicle.door_status,
                robot_mode: message.robot_mode,
                supervision_mode: message.supervision_mode,
                latitude: message.geo_position.lat,
                longitude: message.geo_position.lon,
              }
            : vehicle,
        );

        return { ...oldData, data: updatedData };
      },
    );
  }

  private processMissionMessage(message: MissionMessage, missions: IMission[]): IMission[] {
    return missions.map(mission => {
      if (mission.provider_identifier !== message.provider_id) {
        return mission;
      }

      return {
        ...mission,
        lifecycle: message.lifecycle ?? mission.lifecycle,
        progress: message.progress !== undefined ? message.progress.toString() : mission.progress,
        estimated_distance: message.estimated_distance ?? mission.estimated_distance,
        estimated_duration: message.estimated_duration ?? mission.estimated_duration,
        remaining_distance: message.remaining_distance ?? mission.remaining_distance,
        remaining_duration: message.remaining_duration ?? mission.remaining_duration,
      };
    });
  }

  private dispatchMission(message: MissionMessage) {
    const queries = this.queryClient.getQueriesData<IMission[]>({
      queryKey: ['missions'],
    });

    const missionExists = queries.some(([, missions]) =>
      missions?.some(mission => mission.provider_identifier === message.provider_id),
    );

    if (!missionExists) {
      this.queryClient.invalidateQueries({
        queryKey: ['missions'],
        refetchType: 'active',
      });
      return;
    }

    this.queryClient.setQueriesData<IMission[]>({ queryKey: ['missions'] }, old => {
      if (!old) return old;

      return this.processMissionMessage(message, old);
    });
  }

  private dispatchRoadmap(message: RoadmapMessage) {
    const key = ['roadmap', this.territorySlug, message.vehicle];
    this.queryClient.setQueryData(key, (old: RoadmapMessage) => ({
      ...old,
      ...message,
    }));
  }

  private dispatchAlert() {
    this.queryClient.invalidateQueries({ queryKey: ['alert', this.territorySlug] });
  }
}
